import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * ScrollManager — proper scroll restoration for a React-Router (declarative)
 * SPA. Replaces the old naive "always scroll to top" behaviour which broke
 * Back/Forward restoration.
 *
 * Behaviour:
 *   - New navigation (PUSH / REPLACE): scroll to top (respecting #anchors).
 *   - Back / Forward (POP): restore the exact scroll position that was saved
 *     for that history entry.
 *   - Positions are keyed by the history entry (location.key) and kept in
 *     sessionStorage so they survive reloads and tab switches within a session.
 *
 * Async pages (e.g. the Browse grid with sponsored SmartSlots that inject
 * cells ~500ms after mount) shift layout AFTER an initial restore. To avoid
 * cumulative drift we HOLD the target through a short settle window,
 * re-asserting it every frame (both directions), suppress position-saving
 * while restoring so a shifted value is never persisted, and bail instantly
 * on genuine user scroll so we never fight the reader.
 */
const PREFIX = 'if_scroll:';
const SETTLE_MS = 1800;

const savePos = (key, y) => {
  try { sessionStorage.setItem(PREFIX + key, String(y)); } catch (_) { /* quota / private mode */ }
};
const loadPos = (key) => {
  try {
    const v = sessionStorage.getItem(PREFIX + key);
    return v == null ? null : parseInt(v, 10);
  } catch (_) { return null; }
};

export const ScrollToTop = () => {
  const location = useLocation();
  const navType = useNavigationType(); // 'POP' | 'PUSH' | 'REPLACE'
  const key = location.key || 'default';
  const curKeyRef = useRef(key);
  curKeyRef.current = key;
  // While true, the scroll listener must NOT persist positions (async layout
  // shifts during restoration would otherwise corrupt the saved value).
  const restoringRef = useRef(false);
  const rafRef = useRef(0);

  // Take over the browser's automatic restoration.
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      const prev = window.history.scrollRestoration;
      window.history.scrollRestoration = 'manual';
      return () => { window.history.scrollRestoration = prev; };
    }
    return undefined;
  }, []);

  // Persist the CURRENT entry's scroll position continuously. Mounted once so
  // a route change never triggers a cleanup that saves a just-reset (0) value.
  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (restoringRef.current) return; // don't record during restore/settle
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        savePos(curKeyRef.current, window.scrollY);
        ticking = false;
      });
    };
    const onHide = () => { if (!restoringRef.current) savePos(curKeyRef.current, window.scrollY); };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('pagehide', onHide);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('pagehide', onHide);
    };
  }, []);

  // On each navigation: restore (POP) or reset to top (PUSH / REPLACE).
  useLayoutEffect(() => {
    // Cancel any in-flight restore from a previous navigation.
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    restoringRef.current = false;

    if (location.hash) return; // let the browser handle anchor jumps

    if (navType === 'POP') {
      const y = loadPos(key);
      if (y != null && y > 0) {
        restoreWithSettle(y);
        return;
      }
    }
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Hold `y` through async layout settling; stop on user input or timeout.
  function restoreWithSettle(y) {
    restoringRef.current = true;
    const start = performance.now();
    let interrupted = false;
    const onUser = () => { interrupted = true; };
    window.addEventListener('wheel', onUser, { passive: true });
    window.addEventListener('touchstart', onUser, { passive: true });
    window.addEventListener('keydown', onUser);

    const cleanup = () => {
      window.removeEventListener('wheel', onUser);
      window.removeEventListener('touchstart', onUser);
      window.removeEventListener('keydown', onUser);
      restoringRef.current = false;
      rafRef.current = 0;
      // Re-affirm the intended position under this key (unchanged by drift).
      savePos(key, y);
    };

    const step = () => {
      if (interrupted) { cleanup(); return; }
      const maxY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      const target = Math.min(y, maxY);
      if (Math.abs(window.scrollY - target) > 1) window.scrollTo(0, target);
      if (performance.now() - start < SETTLE_MS) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        cleanup();
      }
    };
    rafRef.current = requestAnimationFrame(step);
  }

  return null;
};

export default ScrollToTop;
